import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@/contexts/auth-context'
import { Button } from '@/components/Button/Button'
import { LoadingIcon } from '@/components/LoadingIcon'
import { CadcContractNotice } from '@/components/CadcContractNotice'
import { DropdownMenuSelect } from '@/components/widget-components'
import {
  getInitialQuoteAnchorSide,
  TransactionType,
  useBuySellStore,
} from '@/reducers/transactionReducer'
import { BuySellSwitcher } from './BuySellSwitcher'
import { FiatCoinRow, StableCoinRow } from './CoinChooser'
import { PaymentMethodSelector } from './Transact/PaymentMethodSelector'
import { InfoPage } from './InfoPage'
import { SubTotal } from './Subtotal'
import { WidgetLayout } from './WidgetLayout'
import {
  NetworkValue,
  NetworkOptions,
  MAX_9999_SELF_SERVE_AMOUNT,
  PaymentTypes,
  StableCoins,
  StableCoinValue,
} from '@/utils/currencies'
import { useUserPreferences } from '@/hooks/useUserPreferences'
import { useMaintenanceStatus } from '@/hooks/useMaintenanceStatus'
import { useLimits } from '@/hooks/useLimits'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { useQuoteV2 } from '@/hooks/useQuoteV2'
import { track } from '@/utils/analytics'
import { createTransactionSearchParams } from './transactionUrl'
import {
  resolveSelfServeMaxCad,
  requiresLargeOrderPaymentChoice,
  formatSelfServeMaxTooltipLabel,
} from './utils'
import { getIsCadcBlockedOnNetwork } from '@/utils/cadc'

export const BuySell = () => {
  const { i18n, t } = useTranslation()
  const [showInfoPage, setShowInfoPage] = useState(false)

  const { isAuth } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useUserPreferences()
  const { isMaintenanceActive } = useMaintenanceStatus('atb')

  const {
    enableBase,
    enableOtc,
    enable25kLimitIncrease,
    enableSendNewCadc,
    enableRejectOldCadc,
    isLoading: featureFlagsLoading,
  } = useFeatureFlags()
  const limits = useLimits()

  const network = useBuySellStore.use.network()
  const transactionType = useBuySellStore.use.transactionType()
  const setTransactionType = useBuySellStore.use.setTransactionTypeV2()
  const requestType = useBuySellStore.use.requestType()
  const setRequestType = useBuySellStore.use.setRequestType()
  const [stableCoinAmount, fiatAmount, stableCoin] = [
    useBuySellStore.use.stableCoinAmount(),
    useBuySellStore.use.fiatAmount(),
    useBuySellStore.use.stableCoin(),
  ]
  const [setStableCoinAmount, setFiatAmount] = [
    useBuySellStore.use.setStableCoinAmountV2(),
    useBuySellStore.use.setFiatAmountV2(),
  ]
  const loading = useBuySellStore.use.loading()
  const reset = useBuySellStore.use.reset()
  const setInputsDisabled = useBuySellStore.use.setInputsDisabled()
  const [showToolTip, setShowToolTip] = useState(false)
  const [anchorSide, setAnchorSide] = useState<'left' | 'right'>(
    getInitialQuoteAnchorSide
  )

  useQuoteV2(anchorSide)

  const handleMouseEnter = () => setShowToolTip(true)
  const handleMouseLeave = () => setShowToolTip(false)

  const maxSelfServeAmount = resolveSelfServeMaxCad(
    limits,
    enable25kLimitIncrease
  )

  const inLargeOrderBand = requiresLargeOrderPaymentChoice({
    fiatAmount,
    transactionType,
    enableOtc,
    enable25kLimitIncrease,
    maxSelfServeCad: maxSelfServeAmount,
  })
  const isCadcTradeBlocked =
    stableCoin === StableCoins.CADC &&
    getIsCadcBlockedOnNetwork({
      network,
      transactionType,
      enableSendNewCadc,
      enableRejectOldCadc,
    })
  const isValidTrade =
    stableCoinAmount > 0 && fiatAmount > 0 && !isCadcTradeBlocked
  const isOtcOrder =
    enableOtc &&
    (fiatAmount > maxSelfServeAmount ||
      (inLargeOrderBand && requestType === PaymentTypes.WIRE))

  useEffect(() => {
    if (
      fiatAmount <= MAX_9999_SELF_SERVE_AMOUNT &&
      requestType === PaymentTypes.WIRE
    ) {
      setRequestType(PaymentTypes.REQUEST)
    }
  }, [fiatAmount, requestType, setRequestType])

  const urlParams = new URLSearchParams(window.location.search)
  const isFlowLocked =
    sessionStorage.getItem('lockedFlow') === 'true' ||
    urlParams.get('lockedFlow') === 'true'

  useEffect(() => {
    if (location.state?.fromOtc) return
    const urlParams = new URLSearchParams(window.location.search)
    const LSLParams = urlParams.get('leftSideLabel')
    const RSLParams = urlParams.get('rightSideLabel')
    reset()
    if (!LSLParams && !RSLParams) {
      setTransactionType(TransactionType.BUY)
    }
  }, [])

  // Fetch query params and set sessionStorage fields accordingly
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const LSVParams = urlParams.get('leftSideValue')
    const RSVParams = urlParams.get('rightSideValue')
    const LSLParams = urlParams.get('leftSideLabel')
    const RSLParams = urlParams.get('rightSideLabel')
    const emailParam = urlParams.get('email')
    const referralCodeParam = urlParams.get('ref')
    const phoneParam = urlParams.get('phone')
    const fNameParam = urlParams.get('firstName')
    const lNameParam = urlParams.get('lastName')
    const dob1Param = urlParams.get('dob1')
    const dob2Param = urlParams.get('dob2')
    const dob3Param = urlParams.get('dob3')
    const addr1Param = urlParams.get('address1')
    const addr2Param = urlParams.get('address2')
    const cityParam = urlParams.get('city')
    const provParam = urlParams.get('province')
    const postalParam = urlParams.get('postal')
    const walletAddressParam = urlParams.get('addr')
    const inputsDisabledParam = urlParams.get('inputsDisabled')
    const langParam = urlParams.get('lang')
    const lockedFlowParam = urlParams.get('lockedFlow')
    const lockNetworkParam = urlParams.get('lockNetwork')
    const autoConnectParam = urlParams.get('autoConnect')
    const externalSessionIdParam = urlParams.get('externalSessionId')

    sessionStorage.setItem('leftSideValue', LSVParams || '')
    sessionStorage.setItem('rightSideValue', RSVParams || '')
    sessionStorage.setItem('leftSideLabel', LSLParams || '')
    sessionStorage.setItem('rightSideLabel', RSLParams || '')
    sessionStorage.setItem('walletAddress', walletAddressParam || '')
    sessionStorage.setItem('inputsDisabled', inputsDisabledParam || '')

    if (autoConnectParam) {
      sessionStorage.setItem('autoConnect', autoConnectParam)
      if (autoConnectParam === 'true') {
        sessionStorage.setItem('lockNetwork', 'true')
      }
    }

    if (emailParam) {
      localStorage.setItem('email', emailParam || '')
    }
    if (referralCodeParam) {
      localStorage.setItem('ref', referralCodeParam || '')
    }
    if (phoneParam) {
      localStorage.setItem('phone', phoneParam || '')
    }
    if (fNameParam) {
      localStorage.setItem('firstName', fNameParam || '')
    }
    if (lNameParam) {
      localStorage.setItem('lastName', lNameParam || '')
    }
    if (dob1Param) {
      localStorage.setItem('dob1', dob1Param || '')
    }
    if (dob2Param) {
      localStorage.setItem('dob2', dob2Param || '')
    }
    if (dob3Param) {
      localStorage.setItem('dob3', dob3Param || '')
    }
    if (addr1Param) {
      localStorage.setItem('address1', addr1Param || '')
    }
    if (addr2Param) {
      localStorage.setItem('address2', addr2Param || '')
    }
    if (cityParam) {
      localStorage.setItem('city', cityParam || '')
    }
    if (provParam) {
      localStorage.setItem('province', provParam || '')
    }
    if (postalParam) {
      localStorage.setItem('postal', postalParam || '')
    }
    if (langParam) {
      localStorage.setItem('lang', langParam || 'en')
      i18n.changeLanguage(langParam)
    }
    if (lockedFlowParam) {
      sessionStorage.setItem('lockedFlow', lockedFlowParam)
    }
    if (lockNetworkParam) {
      sessionStorage.setItem('lockNetwork', lockNetworkParam)
    }
    if (externalSessionIdParam) {
      sessionStorage.setItem(
        'externalSessionId',
        externalSessionIdParam.slice(0, 255)
      )
    }
  }, [i18n, transactionType])

  // Handle inputsDisabled parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const inputsDisabledParam = urlParams.get('inputsDisabled')

    if (inputsDisabledParam === 'true') {
      setInputsDisabled(true)
    }
  }, [])

  useEffect(() => {
    if (isMaintenanceActive) {
      track('maintenance_banner_shown', { service: 'atb' })
    }
  }, [isMaintenanceActive])

  const applyLimit = (bound: 'minimum' | 'maximum') => {
    if (!limits) return
    if (transactionType === TransactionType.BUY) {
      setFiatAmount(limits.leftSideCurrency.CAD[bound])
    } else {
      setStableCoinAmount(limits.leftSideCurrency[stableCoin][bound])
    }
    setAnchorSide('left')
  }

  const setMax = (transactionType: string, coin: StableCoinValue) => {
    track('max_clicked', {
      tx_type: transactionType,
      coin,
      network,
    })
    applyLimit('maximum')
  }

  const setMin = () => {
    track('min_clicked', {
      tx_type: transactionType,
      coin: stableCoin,
      network,
    })
    applyLimit('minimum')
  }

  if (loading) {
    return (
      <WidgetLayout>
        <div className="my-auto flex justify-center">
          <LoadingIcon />
        </div>
      </WidgetLayout>
    )
  }

  return (
    <WidgetLayout>
      {!featureFlagsLoading && !enableBase && (
        <div
          role="status"
          className="mb-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
          data-testid="banner-base-disabled"
        >
          {t('buySell.baseDisabled')}
        </div>
      )}
      <div
        className="flex flex-row justify-between items-center py-25"
        data-testid="text-transaction-header"
      >
        <div className="flex gap-5">
          <BuySellSwitcher visibility={isFlowLocked} />
          <DropdownMenuSelect />
        </div>
      </div>
      <div
        className="relative flex flex-col gap-5"
        data-testid="container-transaction-content"
      >
        <div className="mt-10 md:mt-6">
          {transactionType === TransactionType.BUY && (
            <FiatCoinRow onAmountChange={() => setAnchorSide('left')} />
          )}
          {transactionType === TransactionType.SELL && (
            <StableCoinRow onAmountChange={() => setAnchorSide('left')} />
          )}
        </div>
      </div>
      {!isOtcOrder && (
        <div
          className="flex items-center"
          data-testid="btn-sell-limit-controls"
        >
          <button
            style={{ fontSize: '15px', textDecoration: 'underline' }}
            onClick={() => setMin()}
            disabled={!limits}
            data-testid="btn-sell-min-button"
          >
            {t('buySell.controls.min')}
          </button>
          &nbsp;&nbsp;/&nbsp;&nbsp;
          <div
            className="relative inline-block"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              style={{ fontSize: '15px', textDecoration: 'underline' }}
              onClick={() => setMax(transactionType, stableCoin)}
              disabled={!limits}
              data-testid="btn-sell-max-button"
            >
              {t('buySell.controls.max')}
            </button>
            {showToolTip && (
              <div
                className="absolute z-50 top-1/2 left-full transform -translate-y-1/2 ml-2 px-1 py-1 bg-black text-white text-xs"
                style={{
                  width: '140px',
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word',
                  borderRadius: 0,
                }}
                data-testid="btn-sell-max-tooltip"
              >
                {t(
                  enableOtc
                    ? 'buySell.tooltip.largeOrders'
                    : 'buySell.tooltip.largeOrdersContact',
                  {
                    max: formatSelfServeMaxTooltipLabel(
                      maxSelfServeAmount,
                      i18n.language
                    ),
                  }
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {inLargeOrderBand && (
        <div className="my-1">
          <PaymentMethodSelector maxSelfServeCad={maxSelfServeAmount} />
        </div>
      )}

      {!isOtcOrder && (
        <div
          className="flex-grow p-1 flex flex-col justify-center"
          style={{ maxHeight: '100px' }}
          data-testid="btn-sell-subtotal-container"
        >
          <SubTotal />
        </div>
      )}

      {!isOtcOrder && transactionType === TransactionType.BUY && (
        <StableCoinRow onAmountChange={() => setAnchorSide('right')} />
      )}
      {!isOtcOrder && transactionType === TransactionType.SELL && (
        <FiatCoinRow onAmountChange={() => setAnchorSide('right')} />
      )}

      {isOtcOrder && transactionType === TransactionType.BUY && (
        <StableCoinRow hideAmount />
      )}

      {isOtcOrder && transactionType === TransactionType.SELL && (
        <FiatCoinRow hideAmount />
      )}

      {stableCoin === StableCoins.CADC && (
        <CadcContractNotice
          network={network}
          transactionType={transactionType}
        />
      )}

      {isMaintenanceActive && (
        <Button disabled={true}>{t('buySell.maintenance')}</Button>
      )}

      {!isMaintenanceActive && isOtcOrder && (
        <Button
          disabled={!(fiatAmount > 0) || isCadcTradeBlocked}
          onClick={() => {
            track('quote_accepted', {
              tx_type: transactionType,
              coin: stableCoin,
              network,
              fiat_amount: fiatAmount,
              stable_coin_amount: stableCoinAmount,
              authed: isAuth !== null,
              otc: true,
            })
            if (isAuth !== null) {
              const coinLabel = `${stableCoin}-${network}`
              const isSell = transactionType === TransactionType.SELL
              const otcParams = new URLSearchParams({
                leftSideLabel: isSell ? coinLabel : 'CAD',
                rightSideLabel: isSell ? 'CAD' : coinLabel,
                leftSideValue: String(isSell ? stableCoinAmount : fiatAmount),
              })
              navigate(`/otc-submit?${otcParams.toString()}`)
            } else {
              navigate('/EasyLogin')
            }
          }}
          className="inline-flex items-center py-8 px-4 w-full lg:justify-center lg:gap-2 font-normal"
          style={{ justifyContent: 'center', textAlign: 'center' }}
          data-testid="btn-otc-order-button"
        >
          <span>{t('buySell.button.otc')}</span>
        </Button>
      )}

      {!isMaintenanceActive && !isOtcOrder && (
        <Button
          disabled={!isValidTrade}
          onClick={() => {
            track('quote_accepted', {
              tx_type: transactionType,
              coin: stableCoin,
              network,
              fiat_amount: fiatAmount,
              stable_coin_amount: stableCoinAmount,
              authed: isAuth !== null,
            })
            if (isAuth !== null) {
              const params = createTransactionSearchParams({
                anchorSide,
                currentSearch: window.location.search,
                fiatAmount,
                network,
                stableCoin,
                stableCoinAmount,
                transactionType,
              })
              navigate(`/Transactions?${params.toString()}`)
            } else {
              navigate('/EasyLogin')
            }
          }}
          className="inline-flex items-center py-8 px-4 w-full lg:justify-center lg:gap-2 font-normal"
          style={{ justifyContent: 'center', textAlign: 'center' }}
          data-testid="btn-sell-submit-button"
        >
          <span>
            {transactionType === TransactionType.BUY
              ? t('buySell.button.buy')
              : t('buySell.button.sell')}{' '}
            {(Math.floor(stableCoinAmount * 100) / 100).toFixed(2)} {stableCoin}{' '}
            {t('buySell.button.on')}{' '}
            {NetworkOptions[network as NetworkValue].name}
          </span>
          <span style={{ paddingBottom: '4px', marginLeft: '8px' }}>
            &#x27f6;
          </span>
        </Button>
      )}
      {showInfoPage && (
        <InfoPage
          showBack={true}
          onHide={() => {
            setShowInfoPage(false)
          }}
          data-testid="btn-sell-info-page"
        />
      )}
    </WidgetLayout>
  )
}
